import { useCallback } from 'react';
import { create } from 'zustand';
import type {
  ExperienceConfigName,
  ExperienceTypes,
} from '@/types/gql.generated';
import type { PaywallFeature } from '../gates/gates';
import type { GetRedirectUrlParams, PricingTierName } from '../types';

export type ReturnToModal = 'settings' | 'share';

interface OpenUpgradeModalParams {
  redirect?: GetRedirectUrlParams;
  returnTo?: ReturnToModal;
  experienceType?: ExperienceTypes;
  config?: ExperienceConfigName;
  /**
   * The gate that opened the modal. Carried to the purchase click and through
   * checkout so a conversion can be credited to the feature that sold it.
   */
  feature?: PaywallFeature;
  /**
   * Contextual title for the compare-plans variants, naming the capability that
   * opened the gate. Omit for the generic column-derived heading.
   */
  heading?: string;
  /**
   * Pricing columns to show. Omit to show every tier — that's the full comparison
   * table, which is what a generic "Upgrade" button wants. Gates pass the narrower
   * set their requirement implies.
   */
  tiers?: PricingTierName[];
}

interface UpgradeModalStore {
  isOpen: boolean;
  redirect?: GetRedirectUrlParams;
  returnTo?: ReturnToModal;
  experienceType?: ExperienceTypes;
  config?: ExperienceConfigName;
  feature?: PaywallFeature;
  heading?: string;
  tiers?: PricingTierName[];
  open: (params?: OpenUpgradeModalParams) => void;
  close: () => void;
}

const useUpgradeModalStore = create<UpgradeModalStore>((set) => ({
  isOpen: false,
  redirect: undefined,
  returnTo: undefined,
  experienceType: undefined,
  config: undefined,
  feature: undefined,
  heading: undefined,
  tiers: undefined,
  open: (params?: OpenUpgradeModalParams) =>
    set({
      isOpen: true,
      redirect: params?.redirect,
      returnTo: params?.returnTo,
      experienceType: params?.experienceType,
      config: params?.config,
      feature: params?.feature,
      heading: params?.heading,
      tiers: params?.tiers,
    }),
  close: () =>
    set({
      isOpen: false,
      redirect: undefined,
      returnTo: undefined,
      experienceType: undefined,
      config: undefined,
      feature: undefined,
      heading: undefined,
      tiers: undefined,
    }),
}));

export const useUpgradeModal = () => {
  const {
    isOpen,
    redirect,
    returnTo,
    experienceType,
    config,
    feature,
    heading,
    tiers,
    open,
    close,
  } = useUpgradeModalStore();

  const openUpgradeModal = useCallback(
    (params?: OpenUpgradeModalParams) => {
      open(params);
    },
    [open]
  );

  return {
    isOpen,
    redirect,
    returnTo,
    experienceType,
    config,
    feature,
    heading,
    tiers,
    openUpgradeModal,
    closeUpgradeModal: close,
  };
};
